import { ReactNode, useCallback, useEffect, useRef } from 'react';

import { ReferenceLeftDrawer } from './reference-left-drawer';
import { UseAnchorDocFindingsResult } from './use-anchor-doc';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';

interface AnchorDocViewerProps<T extends { id: string }> {
  // Output of the `useAnchorDocFindings` hook.
  findings: UseAnchorDocFindingsResult;
  // Full domain items in their original shape — sorted internally via the hook's `sortedItemIds`.
  items: T[];

  // Render function for each item in the right hand list
  renderItem: (
    item: T,
    ctx: {
      isActive: boolean;
      onActivate: () => void;
      registerRef: (el: HTMLElement | null) => void;
    },
  ) => ReactNode;

  header?: ReactNode;

  // Rendered above the item list in the right hand column.
  title?: string;
}

/**
 * Two-column layout: doc on the left, item list on the right.
 */
export function AnchorDocViewer<T extends { id: string }>({
  findings,
  items,
  renderItem,
  header,
  title,
}: AnchorDocViewerProps<T>) {
  const itemById = new Map(items.map((item) => [item.id, item]));
  const cardRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    if (!findings.activeItemId) return;
    cardRefs.current.get(findings.activeItemId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [findings.activeItemId]);

  const registerRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) cardRefs.current.set(id, el);
      else cardRefs.current.delete(id);
    },
    [],
  );

  // Highlights are keyed by reference id; clicking one should activate the item
  // that cites that exact reference.
  const handleHighlightClick = useCallback(
    (referenceId: string) => {
      findings.selectItemByReferenceId(referenceId);
    },
    [findings],
  );

  return (
    <div className="flex h-full w-full flex-col border-l bg-gray-50">
      {header}

      <div className="flex flex-1 overflow-hidden">
        <div className="relative w-3/5 border-r bg-gray-100">
          {findings.anchorDoc ? (
            <PDFViewer
              {...findings.anchorDoc}
              highlights={findings.highlights}
              highlightedFactId={findings.activeHighlightId}
              onHighlightClick={handleHighlightClick}
              hideFeatureButtons
              hideSnippingTool
            />
          ) : findings.isLoadingAnchorDoc ? (
            <div className="flex h-full w-full items-center justify-center">
              <Icon className="lucide--loader-2 animate-spin text-gray-400 duration-700" />
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <h2 className="mb-3 font-sans text-sm font-semibold text-balance text-gray-900">{title}</h2>
          <ol className="space-y-3">
            {findings.sortedItemIds.map((id) => {
              const item = itemById.get(id);
              if (!item) return null;
              return renderItem(item, {
                isActive: id === findings.activeItemId,
                onActivate: () => findings.setActiveItemId(id),
                registerRef: registerRef(id),
              });
            })}
          </ol>
        </div>
      </div>

      <ReferenceLeftDrawer
        isOpen={findings.drawerReference !== null}
        activeReferenceId={findings.drawerReference?.id ?? null}
        activeReferenceType={findings.drawerReference?.refType ?? null}
        onClose={() => findings.setDrawerReference(null)}
      />
    </div>
  );
}
